import React from 'react';
import { Text, Title, i18n, tokens, Container, Content, Theme, Button } from '@simplybenefits/sb-ui-library';
import { connect } from "react-redux";
import { Grid } from '@mui/material';
import BreadCrumbs from '../../../components/BreadCrumbs.js';
import ContentBlock from '../components/ContentBlock.js';
import ContentBlockSmall from '../components/ContentBlockSmall.js';
import FYIDoctorsTermsAndConditions from './FYIDoctorsTermsAndConditions.js';
import FYIDoctorsBrands from './FYIDoctorsBrands.js';
import { getFyiDoctorsAppointmentsUrl, getContentBlocks } from './FYIDoctors.constants.js';

/**
 * Builds breadcrumb navigation items for the FYidoctors page.
 * @param {Function} onBack - Callback function to navigate back to Simply Perks
 * @returns {Array<Object>} Array of breadcrumb objects with title, onClick, and isHighlighted properties
 */
const buildBreadCrumbs = (onBack) => [
    { title: i18n.t(tokens.labels.perks.simplyPerks), onClick: onBack },
    { title: 'FYidoctors', onClick: null, isHighlighted: true }
];

const styles = {
    buttonStyle: {
        height: 40,
        marginLeft: 5
    },
    eyeExamInnerContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 30
    },
    seeDifferenceText: {
        marginBottom: 15,
        marginTop: 5
    },
    gridContainer: {
        marginBottom: 1
    },
    sectionTitle: {
        marginBottom: 15
    },
    proactiveCareTitle: {
        marginBottom: 15,
        marginTop: 10
    }
};

/**
 * @param {*} link 
 */
const navigateTo = (link) => {
    window.open(link, "_blank");
};

/**
 * Main FYI Doctors perk page component
 * @param {Object} props.history - React Router history object for navigation
 * @param {Object} [props.whitelabel={}] - Whitelabel configuration object containing brand customization settings
 * @returns {JSX.Element} The FYI Doctors perk page
 */
const FYIDoctors = ({
    history,
    whitelabel = {}
}) => {

    /**
     * Handles navigation back to the Simply Perks page
     */
    const onBack = () => history.push('/perks');
    const breadcrumbs = buildBreadCrumbs(onBack);
    const buttonColor = whitelabel.primaryColor || Theme.colors.skyBlue;
    const contentBlocks = getContentBlocks(i18n.locale);
    const fyiDoctorsUrl = getFyiDoctorsAppointmentsUrl(i18n.locale);

    return (
        <Container>
            <Content>
                <Title title={ i18n.t(tokens.labels.perks.fyiDoctorsTitle) } />
                <BreadCrumbs breadcrumbs={breadcrumbs} whitelabel={whitelabel}/>

                <ContentBlock>
                    <Text
                        large
                        bold
                        customStyle={styles.seeDifferenceText}
                    >
                        { i18n.t(tokens.labels.perks.fyiDoctorsSeeTheDifference) }
                    </Text>
                    <Grid container spacing={2} alignItems="center" sx={styles.gridContainer}>
                        <Grid item xs={12} sm>
                            <Text medium>
                                { i18n.t(tokens.labels.perks.fyiDoctorsDifferenceDescription) }
                            </Text>
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <Button
                                transparent
                                customStyle={styles.buttonStyle}
                                onClick={() => navigateTo(fyiDoctorsUrl)}
                            >
                                <Text
                                    medium
                                    bold
                                    color={buttonColor}
                                >
                                    { i18n.t(tokens.labels.perks.fyiDoctorsLearnMore) }
                                </Text>
                            </Button>
                        </Grid>
                    </Grid>
                </ContentBlock>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Text
                            mediumLarge
                            bold
                            customStyle={styles.sectionTitle}
                        >
                            { i18n.t(tokens.labels.perks.fyiDoctorsStartsWithEyeExam) }
                        </Text>
                        <ContentBlock>
                            <Text
                                medium
                                bold
                                customStyle={styles.proactiveCareTitle}
                            >
                                { i18n.t(tokens.labels.perks.fyiDoctorsStartWithProactiveCare) }
                            </Text>
                            <Text medium customStyle={{ marginBottom: 30 }}>
                                { i18n.t(tokens.labels.perks.fyiDoctorsProactiveCareDescription1) }
                            </Text>
                            <Text medium customStyle={{ marginBottom: 30 }}>
                                { i18n.t(tokens.labels.perks.fyiDoctorsProactiveCareDescription2) }
                            </Text>
                            <Button
                                color="primary"
                                customStyle={{
                                    maxWidth: i18n.locale === 'fr' ? 'auto' : '50%',
                                    marginBottom: 2
                                }}
                                onClick={() => navigateTo(fyiDoctorsUrl)}
                            >
                                { i18n.t(tokens.labels.perks.fyiDoctorsBookEyeExam) }
                            </Button>
                        </ContentBlock>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Text
                            mediumLarge
                            bold
                            customStyle={styles.sectionTitle}
                        >
                            { i18n.t(tokens.labels.perks.fyiDoctorsSaveOnFramesTitle) }
                        </Text>

                        {
                            contentBlocks.map((contentBlock) => (
                                <ContentBlockSmall
                                    key={contentBlock.titleText}
                                    Icon={contentBlock.icon}
                                    iconStyle={contentBlock.iconStyle}
                                    titleText={contentBlock.titleText}
                                    description={contentBlock.description}
                                    link={contentBlock.link}
                                    buttonColor={buttonColor}
                                />
                            ))
                        }

                        <Text>
                            { i18n.t(tokens.labels.perks.fyiDoctorsShowYourBenefitsCard) }
                        </Text>
                        <Text>
                            { i18n.t(tokens.labels.perks.fyiDoctorsDiscountsApplyBefore) }
                        </Text>
                    </Grid>
                </Grid>

                <FYIDoctorsBrands />

                <FYIDoctorsTermsAndConditions />
            </Content>
        </Container>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(FYIDoctors);